<template>
  <v-card flat height="100%" width="100%">
    <v-row no-gutters align="start" justify="start" class="mt-3 ml-1">
      <v-col cols="12" class="mb-4">
        <v-row no-gutters>
          <v-col cols="12">
            <v-row align="center" no-gutters>
              <div class="icon-container mr-3">
                <v-icon size="28" color="turquisePanda"> mdi-poll </v-icon>
              </div>
              <span class="font-weight-regular text-capitalize title">
                Analytics
              </span>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
      <v-col cols="12">
        <v-card class="mb-2 pt-2" width="520px" flat>
          <date-picker
            :shortcuts="shortcuts"
            :clearable="false"
            :disabled-date="verifyDisabledDate"
            @change="fetchData"
            style="width: 100%"
            v-model="dateRange"
            confirm
            range
          >
            <template v-slot:input>
              <v-text-field
                :label="$t('message.date')"
                dense
                :value="dateRangeValue"
                outlined
                prepend-inner-icon="mdi-calendar"
                readonly
              ></v-text-field>
            </template>
            <template v-slot:icon-calendar>
              <v-icon style="display: none">mdi-calendar</v-icon>
            </template>
          </date-picker>
        </v-card>
        <v-row class="mt-4" v-if="loadings.fetch">
          <loading-logo :value="250" />
        </v-row>
        <v-card
          class="d-flex flex-row align-center justify-center"
          flat
          height="250px"
          width="100%"
          v-else-if="errors.fetch"
        >
          <ad-block-error-message
            v-if="isAdBlockEnabled"
            :isLoading="loadings.fetch"
            @handleFetchData="fetchData"
          />
          <v-row
            class="flex-column"
            justify="center"
            align="center"
            no-gutters
            v-else
          >
            <span class="mb-4">{{
              $t('message.pages.analytics.errorDefault')
            }}</span>
            <v-btn color="primary" class="ml-3" @click="fetchData">{{
              $t('message.pages.analytics.tryAgain')
            }}</v-btn>
          </v-row>
        </v-card>
        <template v-else>
          <v-row>
            <v-col>
              <v-card>
                <v-col class="pa-6">
                  <v-row>
                    <span class="text-caption text--secondary">{{
                      $t('message.pages.video.metricsPage.views')
                    }}</span>
                  </v-row>
                  <v-row>
                    <span class="text-h4 primary--text">{{
                      Number(totalData.views).toLocaleString()
                    }}</span>
                  </v-row>
                </v-col>
              </v-card>
            </v-col>
            <v-col>
              <v-card>
                <v-col class="pa-6">
                  <v-row>
                    <span class="text-caption text--secondary">
                      {{ $t('message.pages.video.metricsPage.uniqueViews') }}
                    </span>
                  </v-row>
                  <v-row>
                    <span class="text-h4 primary--text">{{
                      Number(totalData.uniqueViews).toLocaleString()
                    }}</span>
                  </v-row>
                </v-col>
              </v-card>
            </v-col>
            <v-col>
              <v-card>
                <v-col class="pa-6">
                  <v-row>
                    <span class="text-caption text--secondary">
                      {{ $t('message.pages.video.metricsPage.plays') }}
                    </span>
                  </v-row>
                  <v-row>
                    <span class="text-h4 primary--text">{{
                      Number(totalData.plays).toLocaleString()
                    }}</span>
                    <span
                      class="d-flex align-end justify-end caption light--text pb-1"
                      >({{ totalData.playRate }}%)</span
                    >
                  </v-row>
                </v-col>
              </v-card>
            </v-col>
            <v-col>
              <v-card>
                <v-col class="pa-6">
                  <v-row>
                    <span class="text-caption text--secondary">
                      {{ $t('message.pages.video.metricsPage.uniquePlays') }}
                    </span>
                  </v-row>
                  <v-row>
                    <span class="text-h4 primary--text">{{
                      Number(totalData.uniquePlays).toLocaleString()
                    }}</span>
                    <span
                      class="d-flex align-end justify-end caption light--text pb-1"
                      >({{ totalData.uniquePlayRate }}%)</span
                    >
                  </v-row>
                </v-col>
              </v-card>
            </v-col>
          </v-row>

          <v-col cols="12">
            <apexchart
              :class="{ darkThemeSvgFix: $vuetify.theme.dark }"
              type="area"
              height="250"
              :options="viewsChartOptions"
              :series="viewChartSeries"
            ></apexchart>
          </v-col>
        </template>
      </v-col>
      <v-col cols="12" class="mt-6">
        <v-divider />
      </v-col>
      <v-col cols="12" class="mt-6">
        <rank-table />
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import analyticsApi from '@/services/analyticsApi'
import VueApexCharts from 'vue-apexcharts'
import { subDays, subMonths, isBefore, format } from 'date-fns'
import i18n from '@/translations/lang'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/locale/pt-br'
import LoadingLogo from '@/components/LoadingLogo.vue'
import RankTable from './RankTable.vue'
import isSameDay from 'date-fns/isSameDay'
import { mapState, mapMutations } from 'vuex'
import AdBlockErrorMessage from '@/components/AdBlockErrorMessage.vue'

export default {
  name: 'Analytics',
  components: {
    DatePicker,
    apexchart: VueApexCharts,
    LoadingLogo,
    RankTable,
    AdBlockErrorMessage,
  },
  data: () => ({
    loadings: {
      fetch: true,
    },
    viewsChartOptions: {
      chart: {
        toolbar: false,
        type: 'area',
        height: 350,
        width: '100%',
        offsetX: 0,
        offsetY: 0,
      },
      dataLabels: {
        enabled: false,
      },
      xaxis: {
        type: 'datetime',
        categories: [],
      },
      tooltip: {
        theme: 'dark',
        x: {
          format: 'dd/MM/yy HH:mm',
        },
        y: {
          formatter: function (value, { seriesIndex, dataPointIndex, series }) {
            if (seriesIndex === 3 && value >= 0) {
              // verifica se o "Y" atual é o de plays únicos
              const uniquePlays = value
              const uniqueViews = series[1][dataPointIndex]
              let playRate = 0
              if (uniquePlays && uniqueViews) {
                playRate = ((uniquePlays * 100) / uniqueViews).toFixed(1)
              }
              return `${value} (${playRate}%)`
            } else if (seriesIndex === 2 && value >= 0) {
              // verifica se o "Y" atual é o de plays
              const plays = value
              const views = series[0][dataPointIndex]
              let playRate = 0
              if (plays && views) {
                playRate = ((plays * 100) / views).toFixed(1)
              }
              return `${value} (${playRate}%)`
            }
            return value
          },
        },
      },
      stroke: {
        curve: 'smooth',
      },
    },
    viewChartSeries: [],
    totalData: {
      views: 0,
      uniqueViews: 0,
      plays: 0,
      uniquePlays: 0,
      playRate: 0,
      uniquePlayRate: 0,
    },
    shortcuts: [
      {
        text: i18n.t('message.pages.video.metricsPage.datepicker.today'),
        onClick() {
          const date = new Date()
          return [date, date]
        },
      },
      {
        text: i18n.t('message.pages.video.metricsPage.datepicker.yesterday'),
        onClick() {
          const date = new Date()

          return [subDays(date, 1), date]
        },
      },
      {
        text: i18n.t('message.pages.video.metricsPage.datepicker.7days'),
        onClick() {
          const date = new Date()

          return [subDays(date, 7), date]
        },
      },
      {
        text: i18n.t('message.pages.video.metricsPage.datepicker.aMonth'),
        onClick() {
          const date = new Date()

          return [subMonths(date, 1), date]
        },
      },
    ],
    errors: {
      fetch: false,
    },
    dateRange: [new Date('2022/01/01'), new Date()],
  }),
  computed: {
    ...mapState({
      isAdBlock: (state) => state.dashboard.isAdBlockEnabled,
    }),
    dateRangeValue() {
      return `${format(this.dateRange[0], 'dd/MM/yyyy')} - ${format(
        this.dateRange[1],
        'dd/MM/yyyy'
      )}`
    },
    isAdBlockEnabled() {
      if (this.isAdBlock) {
        return this.isAdBlock
      }
      return false
    },
  },
  async mounted() {
    if (this.$vuetify.theme.dark) {
      this.viewsChartOptions.tooltip.theme = 'dark'
    } else {
      this.viewsChartOptions.tooltip.theme = 'light'
    }
    this.$root.$on('analyticsDarkTheme', () => {
      location.reload()
    })
    let newDate = new Date()
    let yesterday = newDate.setDate(newDate.getDate() - 1)
    this.dateRange = [subDays(new Date(), 7), yesterday]
    await this.fetchData()
  },
  beforeDestroy() {
    this.$root.$off('analyticsDarkTheme')
  },
  methods: {
    ...mapMutations('dashboard', ['UPDATE_IS_ADBLOCK_ENABLED']),
    verifyDisabledDate(date) {
      let newDate = new Date()
      let yesterday = newDate.setDate(newDate.getDate() - 1)
      const canUse = !isBefore(date, yesterday)
      return canUse
    },
    async fetchData() {
      this.loadings.fetch = true
      try {
        if (isSameDay(this.dateRange[1], new Date())) {
          this.dateRange[1] = subDays(new Date(), 1)
        }
        const response = await analyticsApi.getAccountMetricsV2(
          format(this.dateRange[0], 'yyyy-MM-dd'),
          format(this.dateRange[1], 'yyyy-MM-dd')
        )
        this.UPDATE_IS_ADBLOCK_ENABLED(false)
        const data = response.data
        this.totalData.views = data.totalViews
        this.totalData.uniqueViews = data.totalUniqueViews
        this.totalData.plays = data.totalPlays
        this.totalData.uniquePlays = data.totalUniquePlays
        if (data.totalPlays && data.totalViews)
          this.totalData.playRate = (
            (data.totalPlays * 100) /
            data.totalViews
          ).toFixed(1)
        else this.totalData.playRate = 0
        if (
          data.totalUniqueViews &&
          data.totalUniquePlays &&
          typeof data.totalUniqueViews === 'number' &&
          typeof data.totalUniquePlays === 'number'
        ) {
          this.totalData.uniquePlayRate = (
            (data.totalUniquePlays * 100) /
            data.totalUniqueViews
          ).toFixed(1)
        }
        this.viewChartSeries = [
          {
            name: i18n.t('message.pages.video.metricsPage.views'),
            data: Object.keys(data.viewsData)
              .map((item) => ({
                x: new Date(item.replace(/-/gm, '/')).toISOString(),
                y: data.viewsData[item].view,
              }))
              .sort(function (a, b) {
                return new Date(b.x) - new Date(a.x)
              }),
          },
          {
            name: i18n.t('message.pages.video.metricsPage.uniqueViews'),
            data: Object.keys(data.viewsData)
              .map((item) => ({
                x: new Date(item.replace(/-/gm, '/')).toISOString(),
                y: data.viewsData[item].unique_view,
              }))
              .sort(function (a, b) {
                return new Date(b.x) - new Date(a.x)
              }),
          },
          {
            name: i18n.t('message.pages.video.metricsPage.plays'),
            data: Object.keys(data.viewsData)
              .map((item) => ({
                x: new Date(item.replace(/-/gm, '/')).toISOString(),
                y: data.viewsData[item].play,
              }))
              .sort(function (a, b) {
                return new Date(b.x) - new Date(a.x)
              }),
          },
          {
            name: i18n.t('message.pages.video.metricsPage.uniquePlays'),
            data: Object.keys(data.viewsData)
              .map((item) => ({
                x: new Date(item.replace(/-/gm, '/')).toISOString(),
                y: data.viewsData[item].unique_play,
              }))
              .sort(function (a, b) {
                return new Date(b.x) - new Date(a.x)
              }),
          },
        ]
        this.errors.fetch = false
      } catch (err) {
        console.error(err)
        const errObject = { err }
        const { response } = errObject
        if (!response?.status) {
          this.UPDATE_IS_ADBLOCK_ENABLED(true)
        }
        this.errors.fetch = true
      }
      this.loadings.fetch = false
    },
  },
}
</script>
<style lang="scss">
@import '~vue2-datepicker/scss/index.scss';
.svgSize {
  width: 450px;
}

.darkThemeSvgFix {
  svg text {
    fill: white;
  }
  .apexcharts-legend-text {
    color: white !important;
  }
}
</style>
